import React, { useEffect, useState } from 'react';
import RandevuOlusturModal from '../../components/RandevuOlusturModal';
import FilterPills from '../../components/FilterPills';
import AwaitingClassNotice from '../../components/AwaitingClassNotice';
import { useAuth } from '../../contexts/AuthContext';
import { studentApi } from '../../services/api';
import { AWAITING_APPOINTMENTS_TEXT, studentClassStatus } from '../../utils/classStatus';

interface Appointment {
  id: number | string;
  appointmentDate?: string | null;
  appointment_type?: string | null;
  meeting_type?: string | null;
  format?: string | null;
  status?: string;
  location?: string | null;
  notes?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  subjects?: { name?: string };
  institutions?: { name?: string };
  users_appointments_teacher_user_idTousers?: { first_name?: string; last_name?: string; teacher_profiles?: { specialization?: string | null } | null };
  users_appointments_requester_user_idTousers?: { first_name?: string; last_name?: string };
}

const APPT_TYPE_LABEL: Record<string, string> = {
  advisor: 'Veli Toplantısı',
  teacher: 'Öğretmen Görüşmesi',
  parent: 'Veli Görüşmesi',
  student: 'Öğrenci Görüşmesi',
};
const FORMAT_LABEL: Record<string, string> = { online: 'Online', face_to_face: 'Yüz Yüze', phone: 'Telefon' };
const STATUS_LABEL: Record<string, string> = { confirmed: 'Onaylandı', pending: 'Onay Bekliyor', pending_parent_approval: 'Veli Onayı Bekliyor', cancelled: 'İptal Edildi', completed: 'Tamamlandı' };
type StatusFilter = 'all' | 'pending' | 'confirmed' | 'cancelled' | 'completed';
const FILTER_LABEL: Record<StatusFilter, string> = { all: 'Tümü', pending: 'Onay Bekleyen', confirmed: 'Onaylanan', cancelled: 'İptal Edilen', completed: 'Tamamlanan' };

function formatTimeRange(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  // start_time is stored as DB Time(0) and returned as a full ISO datetime.
  // Use UTC hours/minutes to avoid local timezone shifting the displayed value.
  const hh = d.getUTCHours().toString().padStart(2, '0');
  const mm = d.getUTCMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

// Proximity indicator (no filtering) — flags an upcoming appointment as Bugün /
// Yarın / Bu Hafta. Uses local calendar days; "Bu Hafta" means later than tomorrow
// but on or before this calendar week's Sunday. Past / farther-out dates → null.
type Proximity = { label: string; bg: string; text: string };
function proximityBadge(dateStr: string | null | undefined): Proximity | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diffDays = Math.round((startOfDay(d).getTime() - startOfDay(new Date()).getTime()) / 86400000);
  if (diffDays === 0) return { label: 'Bugün', bg: 'bg-[#e5599b]', text: 'text-white' };
  if (diffDays === 1) return { label: 'Yarın', bg: 'bg-[#f5a623]', text: 'text-white' };
  if (diffDays > 1) {
    const dow = startOfDay(new Date()).getDay(); // 0 = Pazar
    const daysToSunday = dow === 0 ? 0 : 7 - dow;
    if (diffDays <= daysToSunday) return { label: 'Bu Hafta', bg: 'bg-[#3973bf]', text: 'text-white' };
  }
  return null;
}

const StudentAppointments: React.FC = () => {
  const { user } = useAuth();
  // Kurumlu but in no class: randevu is made with the class's teachers, so creating one
  // is closed (the backend refuses it too) and the notice says why. The list still
  // renders — the kurum can create appointments for the student.
  const awaiting = studentClassStatus(user) === 'awaiting';
  const [modalOpen, setModalOpen] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<Appointment | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [formatFilter, setFormatFilter] = useState<'all' | 'face_to_face' | 'online' | 'phone'>('all');
  // B8: sıralama — randevu tarihine göre yeni→eski (varsayılan, backend sırasıyla aynı) / eski→yeni.
  const [sortOrder, setSortOrder] = useState<'date_desc' | 'date_asc'>('date_desc');

  const load = () => {
    setLoading(true);
    studentApi.appointments()
      .then((res) => setAppointments(res.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <h1 className="text-[20px] lg:text-[24px] font-semibold text-[#2b2b2b]">Randevularım</h1>
        <span className="text-[14px] lg:text-[16px] text-gray-500">Kurumunuz otomatik randevu oluşturabilir</span>
        <button
          onClick={() => setModalOpen(true)}
          disabled={awaiting}
          title={awaiting ? AWAITING_APPOINTMENTS_TEXT : undefined}
          className="bg-[#3973bf] text-white text-[14px] lg:text-[16px] font-semibold px-5 py-3 rounded-[12px] flex items-center gap-2 hover:bg-blue-700 transition-colors ml-auto disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#3973bf]"
        >
          <span className="text-[16px] lg:text-[20px]">+</span> Randevu Oluştur
        </button>
      </div>

      {awaiting && (
        <div className="mb-5">
          <AwaitingClassNotice compact text={AWAITING_APPOINTMENTS_TEXT} />
        </div>
      )}

      {/* Filters — durum + görüşme formatı. */}
      <div className="mb-5 space-y-3">
        <FilterPills
          label="Durum"
          tone="blue"
          value={statusFilter}
          onChange={setStatusFilter}
          options={(Object.keys(FILTER_LABEL) as StatusFilter[]).map((k) => ({ value: k, label: FILTER_LABEL[k] }))}
        />
        <FilterPills
          label="Format"
          tone="pink"
          value={formatFilter}
          onChange={setFormatFilter}
          options={[{ value: 'all', label: 'Tümü' }, { value: 'face_to_face', label: 'Yüz Yüze' }, { value: 'online', label: 'Online' }, { value: 'phone', label: 'Telefon' }]}
        />
        <FilterPills
          label="Sırala"
          tone="dark"
          value={sortOrder}
          onChange={setSortOrder}
          options={[{ value: 'date_desc', label: 'Tarih: Yeni → Eski' }, { value: 'date_asc', label: 'Tarih: Eski → Yeni' }]}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : (() => {
        const filtered = appointments.filter((a) => {
          if (statusFilter !== 'all' && a.status !== statusFilter) return false;
          if (formatFilter !== 'all' && (a.format ?? a.meeting_type) !== formatFilter) return false;
          return true;
        }).sort((a, b) => {
          const ta = a.appointmentDate ? new Date(a.appointmentDate).getTime() : 0;
          const tb = b.appointmentDate ? new Date(b.appointmentDate).getTime() : 0;
          return sortOrder === 'date_asc' ? ta - tb : tb - ta;
        });
        if (filtered.length === 0) {
          return (
            <p className="text-center text-gray-400 py-16">
              {appointments.length === 0
                ? 'Henüz randevunuz yok. "+ Randevu Oluştur" ile yeni bir randevu planlayın.'
                : 'Filtrelerle eşleşen randevu bulunmuyor.'}
            </p>
          );
        }
        return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((a) => {
            const teacher = a.users_appointments_teacher_user_idTousers;
            const teacherName = teacher ? `${teacher.first_name ?? ''} ${teacher.last_name ?? ''}`.trim() : null;
            const dateStr = a.appointmentDate ? new Date(a.appointmentDate).toLocaleDateString('tr-TR') : '-';
            return (
              <div key={a.id} className="bg-[#fafafa] rounded-[24px] border-2 border-blue-100 p-4 lg:p-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h3 className="text-[16px] lg:text-[18px] font-bold text-[#2b2b2b]">{a.institutions?.name ?? a.subjects?.name ?? 'Randevu'}</h3>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {(() => {
                      const prox = proximityBadge(a.appointmentDate);
                      return prox ? <span className={`text-[11px] font-semibold px-2 py-1 rounded ${prox.bg} ${prox.text}`}>{prox.label}</span> : null;
                    })()}
                    <span className={`text-[11px] font-semibold px-2 py-1 rounded ${
                      a.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                      a.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      a.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>{STATUS_LABEL[a.status ?? ''] ?? a.status ?? ''}</span>
                  </div>
                </div>
                <div className="space-y-1 text-[13px] lg:text-[14px] text-[#2b2b2b] mb-3">
                  {a.appointment_type && <p>Tür: {APPT_TYPE_LABEL[a.appointment_type] ?? a.appointment_type}</p>}
                  {(a.format ?? a.meeting_type) && <p>Görüşme: {FORMAT_LABEL[(a.format ?? a.meeting_type) ?? ''] ?? (a.format ?? a.meeting_type)}</p>}
                  {a.location && <p>Yer: {a.location}</p>}
                  {teacherName && <p>Öğretmen: {teacherName}</p>}
                  <p>Saat: {a.start_time ? formatTimeRange(a.start_time) : '-'}</p>
                  <p>Tarih: {dateStr}</p>
                </div>
                <button
                  onClick={() => setDetail(a)}
                  className="bg-[#3973bf] text-white text-[12px] lg:text-[14px] font-semibold px-5 py-2.5 rounded-[12px] hover:bg-blue-700 transition-colors w-full"
                >
                  Detayları Görüntüle
                </button>
              </div>
            );
          })}
        </div>
        );
      })()}

      <RandevuOlusturModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); load(); }}
        role="student"
      />

      {detail && (() => {
        const teacher = detail.users_appointments_teacher_user_idTousers;
        const teacherName = teacher ? `${teacher.first_name ?? ''} ${teacher.last_name ?? ''}`.trim() : '-';
        const teacherSpec = teacher?.teacher_profiles?.specialization ?? null;
        const requester = detail.users_appointments_requester_user_idTousers;
        const requesterName = requester ? `${requester.first_name ?? ''} ${requester.last_name ?? ''}`.trim() : '-';
        const dateStr = detail.appointmentDate ? new Date(detail.appointmentDate).toLocaleDateString('tr-TR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }) : '-';
        const fmtUtcTime = (s: string | null | undefined) => {
          if (!s) return null;
          const d = new Date(s);
          return d.getUTCHours().toString().padStart(2, '0') + ':' + d.getUTCMinutes().toString().padStart(2, '0');
        };
        const startStr = fmtUtcTime(detail.start_time);
        const endStr = fmtUtcTime(detail.end_time);
        const timeRange = startStr && endStr ? `${startStr} – ${endStr}` : startStr ?? formatTimeRange(detail.appointmentDate);
        const formatKey = (detail.format ?? detail.meeting_type) ?? '';
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setDetail(null)} />
            <div className="relative bg-white rounded-[24px] w-full max-w-[560px] p-8" style={{ boxShadow: '0px 8px 24px rgba(0,0,0,0.2)' }}>
              <button
                onClick={() => setDetail(null)}
                className="absolute top-4 right-4 w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center"
                aria-label="Kapat"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#2b2b2b" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="5" x2="15" y2="15" /><line x1="15" y1="5" x2="5" y2="15" /></svg>
              </button>
              <h2 className="text-[22px] font-bold text-[#3973bf] mb-5">Randevu Detayı</h2>

              <div className="space-y-3 text-[14px] text-[#2b2b2b]">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-500">Durum</span>
                  <span className={`text-[12px] font-semibold px-3 py-1 rounded-full ${
                    detail.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                    detail.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    detail.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>{STATUS_LABEL[detail.status ?? ''] ?? detail.status ?? '-'}</span>
                </div>
                <div className="flex justify-between"><span className="font-semibold text-gray-500">Tür</span><span>{APPT_TYPE_LABEL[detail.appointment_type ?? ''] ?? detail.appointment_type ?? '-'}</span></div>
                <div className="flex justify-between"><span className="font-semibold text-gray-500">Görüşme</span><span>{FORMAT_LABEL[formatKey] ?? formatKey ?? '-'}</span></div>
                <div className="flex justify-between"><span className="font-semibold text-gray-500">Tarih</span><span>{dateStr}</span></div>
                <div className="flex justify-between"><span className="font-semibold text-gray-500">Saat</span><span>{timeRange}</span></div>
                <div className="flex justify-between"><span className="font-semibold text-gray-500">Öğretmen</span><span>{teacherName}{teacherSpec ? ` (${teacherSpec})` : ''}</span></div>
                {detail.subjects?.name && (
                  <div className="flex justify-between"><span className="font-semibold text-gray-500">Ders</span><span>{detail.subjects.name}</span></div>
                )}
                <div className="flex justify-between"><span className="font-semibold text-gray-500">Talep Eden</span><span>{requesterName}</span></div>
                {detail.location && (
                  <div className="flex justify-between"><span className="font-semibold text-gray-500">Yer</span><span>{detail.location}</span></div>
                )}
                {detail.notes && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="font-semibold text-gray-500 mb-2">Notlar</p>
                    <p className="text-[14px] text-[#2b2b2b] whitespace-pre-wrap">{detail.notes}</p>
                  </div>
                )}
              </div>

              <button
                onClick={() => setDetail(null)}
                className="mt-6 w-full bg-[#3973bf] text-white font-semibold py-3 rounded-[12px] hover:bg-blue-700 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default StudentAppointments;
